<template>
  <div v-if="!showEditModal" class="team-modal-overlay">
    <div class="team-modal-content" :class="{'tm-timer-bg': timerActive}">
      <!-- Loading State -->
      <template v-if="loading">
        <div class="tm-body tm-loading-body">
          <div class="gs-loading">
            <i class="fa fa-spinner fa-spin"></i>
            <div style="margin-top: 10px">Initializing Teams...</div>
          </div>
        </div>
      </template>

      <!-- Timer Countdown State -->
      <template v-else-if="timerActive">
        <div class="tm-timer-container">
          <div class="tm-timer-badge">GAME ON STARTING</div>
          <div class="tm-timer-subtitle">You are going to interactive Game Mode in ...</div>
          <div class="tm-timer-box">
            <div class="tm-timer-number">{{ timerCount }}</div>
          </div>
        </div>
      </template>

      <!-- Default Preview State -->
      <template v-else>
        <div class="tm-header">
          <div class="tm-header-left">
            <div class="tm-icon-box"><i class="fa fa-gamepad"></i></div>
            <div class="tm-header-text">
              <div class="tm-title">START GAME</div>
              <div class="tm-subtitle">CONFIGURE BREAKOUT ROOMS</div>
            </div>
          </div>
          <div class="tm-close" @click="closeModal"><i class="fa fa-times"></i></div>
        </div>

        <div class="tm-body">
          <div class="tm-section-header">
            <span class="tm-bar"></span> TEAM PREVIEW
            <div class="tm-teams-count">{{ teams.length }} TEAMS</div>
          </div>

          <div class="tm-teams-list">
            <div v-for="team in teams" :key="team.id" class="tm-team-card" :style="{borderColor: team.color}" @click="toggleTeam(team.id)">
              <div class="tm-team-card-main">
                <div class="tm-team-title" :style="{color: team.color}">{{ team.name }}</div>

                <div v-if="!isExpanded(team.id)" class="tm-team-members-preview">
                  <template v-for="member in team.members.slice(0, 3)">
                    <div :key="member.id + '-img'" v-if="member.avatar" class="tm-avatar-img" :style="{backgroundImage: `url(${member.avatar})`}"></div>
                    <div :key="member.id + '-txt'" v-else class="tm-avatar" :style="{backgroundColor: getContrastColor(member.colorIndex)}">
                      {{ getInitials(member.name) }}
                    </div>
                  </template>
                  <div v-if="team.members.length > 3" class="tm-avatar tm-more">+{{ team.members.length - 3 }}</div>
                </div>

                <div class="tm-dropdown-icon">
                  <i class="fa" :class="isExpanded(team.id) ? 'fa-caret-up' : 'fa-caret-down'" :style="{color: team.color}"></i>
                </div>
              </div>

              <div v-if="isExpanded(team.id)" class="tm-team-expanded">
                <div v-for="member in team.members" :key="member.id" class="tm-member-preview-row">
                  <div class="tm-member-info">
                    <div v-if="member.avatar" class="tm-avatar-img" :style="{backgroundImage: `url(${member.avatar})`}"></div>
                    <div v-else class="tm-avatar" :style="{backgroundColor: getContrastColor(member.colorIndex)}">
                      {{ getInitials(member.name) }}
                    </div>
                    <span class="tm-member-name">{{ member.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tm-footer-preview">
          <button class="tm-btn-outline" @click="showEditModal = true"><i class="fa fa-edit" style="margin-right: 6px"></i> EDIT TEAMS</button>
          <button class="tm-btn-primary" @click="saveTeams">SAVE TEAMS <i class="fa fa-play" style="font-size: 10px; margin-left: 4px"></i></button>
        </div>
      </template>
    </div>
  </div>

  <TeamEditModal v-else :initialTeams="teams" :coreColors="coreColors" @cancel="showEditModal = false" @save="handleSaveTeams" />
</template>

<script>
import TeamEditModal from './TeamEditModal.vue'
import {mapGetters} from 'vuex'

const BLOOM_LABELS = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create']

const CORE_COLORS = [
  '#FF3366',
  '#FF9933',
  '#FFCC00',
  '#33CC33',
  '#3399FF',
  '#9933FF',
  '#FF66B2',
  '#FFB266',
  '#FFE566',
  '#66E566',
  '#66B2FF',
  '#B266FF',
  '#FF0000',
  '#FF8000',
  '#FFFF00',
  '#00FF00',
  '#0000FF',
  '#8000FF',
  '#CC0000',
  '#CC6600',
  '#CCCC00',
  '#00CC00',
  '#0000CC',
  '#6600CC',
  '#FF4D4D',
  '#FFA64D',
  '#FFFF4D',
  '#4DFF4D',
  '#4D4DFF',
  '#A64DFF',
]

export default {
  name: 'TeamPreviewModal',

  components: {TeamEditModal},

  data() {
    return {
      showEditModal: false,
      timerActive: false,
      timerCount: 3,
      loading: true,
      teams: [],
      expandedTeams: new Set(),
      gameConfig: null,
      coreColors: CORE_COLORS,
    }
  },

  computed: {
    ...mapGetters(['getPageById', 'sessionId']),
    ...mapGetters('teacher', ['questions']),

    slides() {
      return (this.questions || []).map((q) => {
        const titleType = q.type === 'choice' ? 'MULTIPLE CHOICE' : q.type?.toUpperCase() || 'TEXT'
        const bloomIndex = q.bloom?.[0] ?? null
        return {
          id: q._id || q.id,
          page: q.page,
          title: `${titleType} QUESTION`,
          level: bloomIndex,
          type: bloomIndex !== null ? BLOOM_LABELS[bloomIndex]?.toUpperCase() : null,
          points: q.score?.points || 2,
          pic: this.getPageById(q.page)?.pic || '',
          cpa: q.cpa || null,
          abstractData: q.abstract?.data || '',
          options: q.options || [],
        }
      })
    },
  },

  async mounted() {
    document.body.appendChild(this.$el)
    await this.initGameOnRecord()
  },

  destroyed() {
    this.$el?.parentNode?.removeChild(this.$el)
  },

  methods: {
    // ─── Helpers ──────────────────────────────────────────────────────────────

    isExpanded(teamId) {
      return this.expandedTeams.has(teamId)
    },

    toggleTeam(teamId) {
      const next = new Set(this.expandedTeams)
      next.has(teamId) ? next.delete(teamId) : next.add(teamId)
      this.expandedTeams = next
    },

    getInitials(name = '') {
      const parts = name.trim().split(' ')
      return parts.length > 1 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
    },

    getContrastColor(index = 0) {
      return CORE_COLORS[index % CORE_COLORS.length] || '#808080'
    },

    closeModal() {
      this.$emit('close')
    },

    // ─── Init ─────────────────────────────────────────────────────────────────

    async initGameOnRecord() {
      this.loading = true
      try {
        if (!window.App) return

        let record
        const existingId = this.$store.state.session.gameOn?.teamGameId
        if (existingId) {
          try {
            record = await App.service('game-on').get(existingId)
          } catch (e) {
            console.warn('Failed to fetch existing game-on record, creating new one')
          }
        }

        if (!record) {
          const pageIds = this.slides.map((s) => s.page)
          record = await App.service('game-on').create({
            sessionId: this.sessionId,
            pageIds,
            timePerQuestion: 300,
          })

          await this.$store.dispatch('upSession', {
            gameOn: {
              ...(this.$store.state.session.gameOn || {}),
              teamGameId: record._id,
            },
          })
        }

        this.gameConfig = {gameOnRecord: record}
        this.parseGameOnData()
      } catch (err) {
        console.error('TeamPreviewModal: Failed to init game-on record', err)
        this.$message.error('Failed to initialize teams. Please try again.')
      } finally {
        this.loading = false
      }
    },

    parseGameOnData() {
      const record = this.gameConfig?.gameOnRecord
      if (!record) return

      const dbTeams = record.teams || []
      const dbMembers = record.members || []
      const classMembers = this.$store?.state?.teacher?.rooms?.members || []

      let colorIdx = 0
      this.teams = dbTeams
        .map((t, idx) => ({
          id: t.id,
          name: t.name || `TEAM ${t.id}`,
          score: t.score || 0,
          progress: t.progress || 0,
          color: CORE_COLORS[idx % CORE_COLORS.length],
          members: dbMembers
            .filter((m) => m.teamId === t.id)
            .map((m) => {
              const user = classMembers.find((c) => String(c._id) === String(m.uid) || String(c.id) === String(m.uid)) || {}
              let mName = typeof m.name === 'string' ? m.name : Array.isArray(m.name) ? m.name.filter(Boolean).join(' ') : null
              const uName = user.nickname || (Array.isArray(user.name) ? user.name.filter(Boolean).join(' ') : user.name) || user.email
              return {
                id: m.uid,
                name: mName || uName || 'Student',
                avatar: m.avatar || user.avatar || user.pic || '',
                score: m.score || 0,
                originalMemberData: m,
                colorIndex: colorIdx++,
              }
            }),
        }))
        .filter((t) => t.members && t.members.length > 0)
    },

    // ─── Game Start ───────────────────────────────────────────────────────────

    async startGame() {
      if (this.timerActive) return

      this.timerActive = true
      this.timerCount = 3

      const record = this.gameConfig?.gameOnRecord
      if (window.App && record?._id) {
        try {
          const patchedRecord = await App.service('game-on').patch(record._id, {status: 1, winnerId: null})
          this.$set(this.gameConfig, 'gameOnRecord', patchedRecord)

          App.service('auth').patch('syncTeacher', {
            commit: 'student/startGameOn',
            data: {
              _id: record._id,
              teams: this.teams,
              timeRemaining: patchedRecord.expiresAt,
            },
          })
        } catch (err) {
          console.error('Failed to patch game-on status:', err)
        }
      }

      const finalPayload = {recordId: record._id, teams: this.teams, gameOnRecord: this.gameConfig.gameOnRecord}
      const interval = setInterval(() => {
        this.timerCount--
        if (this.timerCount === 0) {
          clearInterval(interval)
          this.timerActive = false
          this.$emit('start', finalPayload)
        }
      }, 1000)
    },

    saveTeams() {
      this.$emit('save-teams')
      this.closeModal()
    },

    // ─── Save Teams ───────────────────────────────────────────────────────────

    handleSaveTeams(newTeams) {
      const validTeams = (newTeams || []).filter((t) => t.members && t.members.length > 0)
      if (this.gameConfig?.gameOnRecord?._id && window.App) {
        const dbTeams = validTeams.map(({id, name, score, progress}) => ({
          id,
          name,
          score: score || 0,
          progress: progress || 0,
        }))

        const dbMembers = validTeams.flatMap((team) =>
          team.members.map((m) => ({
            ...(m.originalMemberData || {}),
            teamId: team.id,
            uid: m.id,
            score: m.score || 0,
          }))
        )

        App.service('game-on')
          .patch(this.gameConfig.gameOnRecord._id, {teams: dbTeams, members: dbMembers})
          .catch((err) => console.error('Failed to patch game-on teams/members:', err))
      }

      this.teams = validTeams
      this.showEditModal = false
    },
  },
}
</script>

<style scoped>
.team-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.team-modal-content {
  width: 800px;
  max-height: 90vh;
  min-height: 300px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #1e293b;
  font-family: Arial, sans-serif;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.tm-timer-bg {
  background-image: linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url('~@/assets/picture/gameOnTimer.png');
  background-size: cover;
  background-position: center;
  border: 2px solid rgba(16, 185, 129, 0.7);
  box-shadow: 0 0 30px rgba(16, 185, 129, 0.15);
}

/* ── Header ── */
.tm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.tm-header-left {
  display: flex;
  align-items: center;
}
.tm-icon-box {
  color: #10b981;
  font-size: 24px;
  margin-right: 12px;
}
.tm-header-text {
  display: flex;
  flex-direction: column;
}
.tm-title {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}
.tm-subtitle {
  font-size: 10px;
  color: #10b981;
  letter-spacing: 0.5px;
}
.tm-close {
  cursor: pointer;
  color: #64748b;
  font-size: 20px;
  transition: color 0.2s;
}
.tm-close:hover {
  color: #1e293b;
}

/* ── Body ── */
.tm-body {
  padding: 30px 40px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.tm-loading-body {
  justify-content: center;
  align-items: center;
}

.tm-section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
  color: #1e293b;
}
.tm-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background-color: #10b981;
  margin-right: 8px;
}
.tm-teams-count {
  margin-left: auto;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  background-color: #f8fafc;
}

/* ── Team Cards ── */
.tm-teams-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.tm-team-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 4px solid;
  border-top: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.tm-team-card:hover {
  background: #f8fafc;
}

.tm-team-card-main {
  display: flex;
  align-items: center;
  padding: 16px 20px;
}
.tm-team-title {
  font-size: 14px;
  font-weight: bold;
  flex: 1;
  min-width: 150px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.tm-team-members-preview {
  display: flex;
  flex: 3;
}
.tm-dropdown-icon {
  cursor: pointer;
  opacity: 0.8;
  font-size: 18px;
}

/* ── Expanded Members ── */
.tm-team-expanded {
  padding: 0 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tm-member-preview-row {
  background: #f1f5f9;
  padding: 10px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
}
.tm-member-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.tm-member-name {
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  color: #1e293b;
}

/* ── Avatars ── */
.tm-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: bold;
  color: #fff;
  border: 2px solid #ffffff;
  margin-left: -6px;
}
.tm-avatar:first-child {
  margin-left: 0;
}
.tm-avatar.tm-more {
  background-color: #cbd5e1 !important;
  color: #1e293b !important;
}

.tm-avatar-img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 2px solid #ffffff;
  margin-left: -6px;
}

/* ── Footer ── */
.tm-footer-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  padding: 15px 40px;
  border-top: 1px solid #e2e8f0;
}
.tm-btn-outline {
  background: transparent;
  border: 1px solid #10b981;
  color: #10b981;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}
.tm-btn-outline:hover {
  background: #ecfdf5;
}
.tm-btn-primary {
  background: #10b981;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s, transform 0.2s;
}
.tm-btn-primary:hover {
  background: #059669;
  transform: scale(1.02);
}

/* ── Timer ── */
.tm-timer-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px;
}
.tm-timer-badge {
  border: 1px solid #10b981;
  border-radius: 20px;
  padding: 6px 20px;
  color: #10b981;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 2px;
}
.tm-timer-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 24px 0 40px;
}
.tm-timer-box {
  width: 320px;
  height: 160px;
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: #f8fafc;
}
.tm-timer-box::before {
  content: '';
  position: absolute;
  inset: -12px;
  border: 1px solid rgba(0, 255, 255, 0.15);
  border-radius: 20px;
}
.tm-timer-number {
  color: #00ffff;
  font-size: 100px;
  font-weight: bold;
  text-shadow: 0 0 20px rgba(0, 255, 255, 0.8);
  line-height: 1;
}

/* ── Loading ── */
.gs-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #00ffff;
  font-size: 18px;
}
</style>
